import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chess } from 'chess.js';
import { ChessBoard } from '../components/ChessBoard';
import { useChessEngine } from '../hooks/useChessEngine';
import { api } from '../utils/api';
import { statsManager } from '../utils/stats';

type GamePhase = 'setup' | 'playing' | 'finished';

export function Play() {
  const navigate = useNavigate();
  const [gameId, setGameId] = useState<string | null>(null);
  const [phase, setPhase] = useState<GamePhase>('setup');
  const [playerColor, setPlayerColor] = useState<'white' | 'black'>('white');
  const [chess] = useState(() => new Chess());
  const [isCreating, setIsCreating] = useState(false);
  
  // Client-side time tracking for live clock updates
  const [whiteTimeMs, setWhiteTimeMs] = useState<number>(10 * 60 * 1000);
  const [blackTimeMs, setBlackTimeMs] = useState<number>(10 * 60 * 1000);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());
  
  const { gameState, connected, error, sendMove } = useChessEngine(gameId);

  // Update local chess state when backend sends updates
  useEffect(() => {
    if (gameState?.fen) {
      chess.load(gameState.fen);
    }
  }, [gameState?.fen, chess]);

  // Handle game over
  useEffect(() => {
    if (gameState?.status === 'finished' && gameState.result) {
      setPhase('finished');
      
      // Record stats
      if (gameState.result === 'white_wins') {
        statsManager.recordGame(playerColor === 'white' ? 'win' : 'loss');
      } else if (gameState.result === 'black_wins') {
        statsManager.recordGame(playerColor === 'black' ? 'win' : 'loss');
      } else if (gameState.result === 'draw') {
        statsManager.recordGame('draw');
      }
    }
  }, [gameState?.status, gameState?.result, playerColor]);

  // Sync server time with client-side clocks
  useEffect(() => {
    if (gameState?.white_time_ms !== undefined && gameState?.black_time_ms !== undefined) {
      setWhiteTimeMs(gameState.white_time_ms);
      setBlackTimeMs(gameState.black_time_ms);
      setLastUpdateTime(Date.now());
    }
  }, [gameState?.white_time_ms, gameState?.black_time_ms]);

  // Client-side clock ticking
  useEffect(() => {
    if (phase !== 'playing' || !gameState) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastUpdateTime;
      
      // Update the clock for the current player
      if (chess.turn() === 'w') {
        setWhiteTimeMs(prev => Math.max(0, prev - elapsed));
      } else {
        setBlackTimeMs(prev => Math.max(0, prev - elapsed));
      }
      
      setLastUpdateTime(now);
    }, 100); // Update every 100ms for smooth countdown

    return () => clearInterval(interval);
  }, [phase, gameState, chess.turn(), lastUpdateTime]);

  const startGame = async () => {
    setIsCreating(true);
    try {
      // Randomly assign color
      const randomColor = Math.random() > 0.5 ? 'white' : 'black';
      setPlayerColor(randomColor);

      // Create game configuration
      const config = {
        white: randomColor === 'white' 
          ? { type: 'human' as const }
          : { type: 'engine' as const, engine_version: '18.4' as const },
        black: randomColor === 'black'
          ? { type: 'human' as const }
          : { type: 'engine' as const, engine_version: '18.4' as const },
        time_control: {
          minutes: 10,
          increment: 0,
        },
      };

      const response = await api.createGame(config);
      setGameId(response.game_id);
      setPhase('playing');
    } catch (err) {
      console.error('Failed to create game:', err);
      alert('Failed to start game. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleMove = (move: { from: string; to: string; promotion?: string }) => {
    const uci = `${move.from}${move.to}${move.promotion || ''}`;
    sendMove(uci);
  };

  const formatTime = (ms?: number) => {
    if (!ms) return '10:00';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getMaterialDifferential = () => {
    const pieceValues: Record<string, number> = {
      'p': 1, 'n': 3, 'b': 3, 'r': 5, 'q': 9,
    };

    let whiteValue = 0;
    let blackValue = 0;

    // Count material for both sides
    const board = chess.board();
    board.forEach(row => {
      row.forEach(square => {
        if (square) {
          const value = pieceValues[square.type.toLowerCase()] || 0;
          if (square.color === 'w') {
            whiteValue += value;
          } else {
            blackValue += value;
          }
        }
      });
    });

    // Calculate differential from player's perspective
    const differential = playerColor === 'white' 
      ? whiteValue - blackValue 
      : blackValue - whiteValue;

    if (differential === 0) return '';
    return differential > 0 ? `+${differential}` : `${differential}`;
  };

  const getResultText = () => {
    if (!gameState?.result || !gameState.result_reason) return '';
    
    let resultText = '';
    if (gameState.result === 'white_wins') {
      resultText = playerColor === 'white' ? 'You Win!' : 'V7P3R Wins!';
    } else if (gameState.result === 'black_wins') {
      resultText = playerColor === 'black' ? 'You Win!' : 'V7P3R Wins!';
    } else {
      resultText = 'Draw!';
    }

    const reasonMap: Record<string, string> = {
      checkmate: 'by checkmate',
      stalemate: 'by stalemate',
      insufficient_material: 'by insufficient material',
      threefold_repetition: 'by threefold repetition',
      fifty_move_rule: 'by fifty-move rule',
      white_time_forfeit: 'white ran out of time',
      black_time_forfeit: 'black ran out of time',
    };

    return `${resultText} ${reasonMap[gameState.result_reason] || ''}`;
  };

  // Setup phase
  if (phase === 'setup') {
    return (
      <div className="min-h-screen bg-black text-[#e0e0e0] flex items-center justify-center p-4">
        <div className="viper-card max-w-md w-full text-center space-y-6">
          <h1 className="text-3xl font-bold text-[#00ff41] font-mono">
            // READY?
          </h1>
          
          <div className="space-y-3 text-left">
            <div className="flex justify-between py-2 border-b border-[#1a1a1a]">
              <span className="text-gray-400">Opponent:</span>
              <span>V7P3R v18.4</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#1a1a1a]">
              <span className="text-gray-400">Time:</span>
              <span>10+0</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-400">Color:</span>
              <span>Random</span>
            </div>
          </div>

          <button
            onClick={startGame}
            disabled={isCreating}
            className="viper-button w-full"
          >
            {isCreating ? 'STARTING...' : 'START GAME'}
          </button>

          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-[#00ff41] transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Playing/Finished phase
  return (
    <div className="min-h-screen bg-black text-[#e0e0e0] p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-[#00ff41] font-mono">
            V7P3R.LABS
          </h1>
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-[#00ff41] transition-colors"
          >
            ← Exit Game
          </button>
        </div>

        {/* Connection Status */}
        {!connected && phase === 'playing' && (
          <div className="bg-yellow-900/20 border border-yellow-500 text-yellow-500 p-3 rounded">
            Connecting to game server...
          </div>
        )}

        {error && (
          <div className="bg-red-900/20 border border-red-500 text-red-500 p-3 rounded">
            Error: {error}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {/* Game Info Sidebar */}
          <div className="md:col-span-1 space-y-4">
            {/* Player Info */}
            <div className="viper-card space-y-3">
              <h2 className="text-lg font-bold text-[#00ff41] font-mono">
                // CLOCKS
              </h2>
              
              <div className="space-y-2">
                {/* Engine clock (top) */}
                <div className="p-3 rounded bg-[#1a1a1a]">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">V7P3R v18.4</span>
                    <span className="text-[#00ff41] font-mono text-lg">
                      {formatTime(playerColor === 'white' ? blackTimeMs : whiteTimeMs)}
                    </span>
                  </div>
                </div>
                
                {/* User clock (bottom) */}
                <div className="p-3 rounded bg-[#1a1a1a] border-2 border-[#00ff41]/30">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">You</span>
                    <span className="text-[#00ff41] font-mono text-lg">
                      {formatTime(playerColor === 'white' ? whiteTimeMs : blackTimeMs)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Game Status */}
            <div className="viper-card">
              <h2 className="text-lg font-bold text-[#00ff41] font-mono mb-3">
                // STATUS
              </h2>
              
              {phase === 'finished' ? (
                <div className="space-y-4">
                  <div className="text-xl font-bold text-[#00ff41]">
                    {getResultText()}
                  </div>
                  <button
                    onClick={() => navigate('/')}
                    className="viper-button w-full"
                  >
                    BACK TO HOME
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-gray-300">
                    {chess.turn() === (playerColor === 'white' ? 'w' : 'b')
                      ? 'Your turn'
                      : 'V7P3R is thinking...'}
                  </div>
                  
                  {/* Material Differential */}
                  {getMaterialDifferential() && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">Material:</span>
                      <span className={`font-mono font-bold ${
                        getMaterialDifferential().startsWith('+')
                          ? 'text-[#00ff41]'
                          : 'text-red-400'
                      }`}>
                        {getMaterialDifferential()}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 text-sm text-gray-400">
                Moves: {gameState?.moves.length || 0}
              </div>
            </div>
          </div>

          {/* Chessboard */}
          <div className="md:col-span-2">
            <ChessBoard
              position={gameState?.fen || chess.fen()}
              onMove={handleMove}
              playerColor={playerColor}
              disabled={phase !== 'playing' || !connected}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
